/**
 * CAD1 launcher: constants and configuration (PLAN cad1-phase3 §5 D1). Nothing here reads the network.
 *
 * Two launchers (Bez 2026-10-09, launcher v2: creation fee 0.005 BNB, minimum first buy setting 0):
 *   NEXT_PUBLIC_CAD1_LAUNCHER_V2_ADDRESS + NEXT_PUBLIC_CAD1_V2_DEPLOY_BLOCK   v2: every new launch goes here (`launcher`, `deployBlock`)
 *   NEXT_PUBLIC_CAD1_LAUNCHER_ADDRESS + NEXT_PUBLIC_CAD1_DEPLOY_BLOCK         v1: retired by setPaused(true), read-only. Its tokens stay
 *                                                                             valid: its registry is read for the list, the token band,
 *                                                                             the tx page and the shadow-pool defence (`registries`)
 * Each registry is per launcher, so `registries` lists both, oldest first (launch numbers run on from v1's into v2's). No v2 address
 * means preview for the create flow: every page renders, figures come from the TypeScript port of the launcher's closed form
 * (logic/quote.ts) with a Preview tag, and nothing can be signed; a v1 address alone still has its registry read.
 * The Stage build also accepts `window.__CAD1_TEST__ = { launcher, deployBlock, v1? }` (launcher = v2), which only a Playwright
 * `addInitScript` sets for the fork e2e (PLAN §1). On a production build `process.env.NEXT_PUBLIC_APP_ENV` is inlined at build time, so
 * that branch is dead code (config.test.ts pins the shape that makes it so). Never a URL, query parameter or storage value.
 *
 * The expected wiring is the BSC mainnet CADINU V3 stack; a deployed launcher whose readbacks differ is "misconfigured" (logic/mode.ts).
 */
import { getAddress, isAddress, type Address } from 'viem'

export const CAD1_CHAIN_ID = 56
/** The CAD1 tier: 0.5 % (hundredths of a bip) on tick spacing 100, whitelisted for the launcher only (Bez, 2026-10-09). */
export const CAD1_FEE = 5000
export const CAD1_TICK_SPACING = 100

export const CAD1_WIRING = {
  factory: '0x2cb62aEBa8eB609020129983A5141DB883abca85',
  positionManager: '0x0C26558A7Bf8be790774fc84De8e5229A4dB5BA1',
  lock: '0x9626aeA51924e1fE519164B47f971f0810e60Bc0',
  wbnb: '0xbb4CdB9CBd36B01bD1cBaEBF2De08d9173bc095c',
} as const

/**
 * The launcher's constants at CAD1_SHA, used by the preview port and by the form checks. A deployed launcher's own reads replace the
 * start cap; the rest are compile-time constants of the contract (config.test.ts compares them with the forge vectors).
 */
export const CAD1_PLANNED = {
  fee: CAD1_FEE,
  tickSpacing: CAD1_TICK_SPACING,
  startCap: 10n * 10n ** 18n,
  totalSupply: 10n ** 27n,
  /** MIN_FIRST_BUY: the fixed floor of any first buy (0.01 BNB); a first buy is 0 or at least this while minFirstBuySetting is 0 */
  firstBuyFloor: 10n ** 16n,
  /** v2 minFirstBuy() at deployment (Bez 2026-10-09: 0 for now); the owner can set 0..maxMinFirstBuy later, read live */
  minFirstBuySetting: 0n,
  maxMinFirstBuy: 10n ** 18n,
  /** v2 creationFee() at deployment (DEFAULT_CREATION_FEE; Bez 2026-10-09: 0.005 BNB for now); the owner can set 0..maxCreationFee */
  creationFee: 5n * 10n ** 15n,
  maxCreationFee: 5n * 10n ** 17n,
  highShareBps: 2000n,
  bps: 10_000n,
  lockDuration: 315_360_000n,
  maxNameBytes: 32,
  maxSymbolBytes: 12,
  maxUriBytes: 256,
  /** the factory's default feeProtocol for a new tier (low 16 bits token0, high 16 bits token1, /10,000): 3200 = 32 % each side */
  feeProtocolDefault: 209_718_400,
} as const

/** The deadline the page signs: latest block time + this many seconds. */
export const CAD1_DEADLINE_SECONDS = 1200n
/** Below this many seconds before the deadline, Review goes back to Details instead of opening the wallet. */
export const CAD1_STALE_SECONDS = 60n
/** USD older than this is hidden. */
export const CAD1_USD_MAX_AGE_MS = 30 * 60_000

/** Next inlines NEXT_PUBLIC_* only when read literally, so they are read here once; the readers below take an env record (tests pass their own). */
export const CAD1_PUBLIC_ENV: Record<string, string | undefined> = {
  NEXT_PUBLIC_CAD1_LAUNCHER_ADDRESS: process.env.NEXT_PUBLIC_CAD1_LAUNCHER_ADDRESS,
  NEXT_PUBLIC_CAD1_DEPLOY_BLOCK: process.env.NEXT_PUBLIC_CAD1_DEPLOY_BLOCK,
  NEXT_PUBLIC_CAD1_LAUNCHER_V2_ADDRESS: process.env.NEXT_PUBLIC_CAD1_LAUNCHER_V2_ADDRESS,
  NEXT_PUBLIC_CAD1_V2_DEPLOY_BLOCK: process.env.NEXT_PUBLIC_CAD1_V2_DEPLOY_BLOCK,
  NEXT_PUBLIC_APP_ENV: process.env.NEXT_PUBLIC_APP_ENV,
}

type SeamBlock = number | string | bigint
/** launcher = v2 (new launches); v1 = an older launcher on the same fork, read-only */
export type Cad1TestSeam = { launcher: string; deployBlock?: SeamBlock; v1?: { launcher: string; deployBlock?: SeamBlock } }
/** A launcher whose registry counts: v1 (read-only since v2) or v2 (new launches). */
export type Cad1Registry = { address: Address; deployBlock: bigint; version: 1 | 2 }
export type Cad1Config = {
  /** checksummed v2 launcher address (every new launch), or '' (preview) */
  launcher: Address | ''
  /** first block to scan for the v2 launcher's Launched logs (0 when unknown) */
  deployBlock: bigint
  /** every launcher whose registry counts, oldest first (v1, then v2); each address once */
  registries: Cad1Registry[]
  source: 'env' | 'seam' | 'none'
  stage: boolean
}

const isStage = (env: Record<string, string | undefined>) => (env.NEXT_PUBLIC_APP_ENV ?? '').trim() === 'stage'

const toAddress = (raw: unknown): Address | '' => {
  const s = typeof raw === 'string' ? raw.trim() : ''
  return s && isAddress(s) ? getAddress(s) : ''
}

const toBlock = (raw: unknown): bigint => {
  if (typeof raw === 'bigint') return raw >= 0n ? raw : 0n
  if (typeof raw === 'number') return Number.isSafeInteger(raw) && raw >= 0 ? BigInt(raw) : 0n
  const s = typeof raw === 'string' ? raw.trim() : ''
  return /^\d{1,12}$/.test(s) ? BigInt(s) : 0n
}

/** v1 (if any) then v2 (if any); the same address in both counts once, as v2. */
const registriesOf = (v1: Address | '', v1Block: bigint, v2: Address | '', v2Block: bigint): Cad1Registry[] => [
  ...(v1 && v1 !== v2 ? [{ address: v1, deployBlock: v1Block, version: 1 as const }] : []),
  ...(v2 ? [{ address: v2, deployBlock: v2Block, version: 2 as const }] : []),
]

/** Pure: the launchers and deploy blocks for this env, plus the Stage-only test seam when given. */
export function resolveCad1Config(env: Record<string, string | undefined> = CAD1_PUBLIC_ENV, seam?: Cad1TestSeam): Cad1Config {
  const stage = isStage(env)
  if (stage && seam) {
    const launcher = toAddress(seam.launcher)
    if (launcher) {
      const deployBlock = toBlock(seam.deployBlock)
      const registries = registriesOf(toAddress(seam.v1?.launcher), toBlock(seam.v1?.deployBlock), launcher, deployBlock)
      return { launcher, deployBlock, registries, source: 'seam', stage }
    }
  }
  const launcher = toAddress(env.NEXT_PUBLIC_CAD1_LAUNCHER_V2_ADDRESS)
  const deployBlock = launcher ? toBlock(env.NEXT_PUBLIC_CAD1_V2_DEPLOY_BLOCK) : 0n
  const registries = registriesOf(toAddress(env.NEXT_PUBLIC_CAD1_LAUNCHER_ADDRESS), toBlock(env.NEXT_PUBLIC_CAD1_DEPLOY_BLOCK), launcher, deployBlock)
  return { launcher, deployBlock, registries, source: registries.length ? 'env' : 'none', stage }
}

/** The registry addresses, oldest first (what the router and the token page gate read). */
export const cad1RegistryAddresses = (config: Pick<Cad1Config, 'registries'>): Address[] => config.registries.map((r) => r.address)

/**
 * The Playwright seam. The literal `process.env.NEXT_PUBLIC_APP_ENV` check must stay the first statement: Next replaces it with the
 * build's value, so a production bundle contains `if ("production" !== 'stage') return undefined` and the window read is removed.
 */
export function readTestSeam(): Cad1TestSeam | undefined {
  if (process.env.NEXT_PUBLIC_APP_ENV !== 'stage') return undefined
  const seam = typeof window === 'undefined' ? undefined : (window as any).__CAD1_TEST__
  if (!seam || typeof seam !== 'object' || typeof seam.launcher !== 'string') return undefined
  const v1 = seam.v1 && typeof seam.v1 === 'object' && typeof seam.v1.launcher === 'string' ? { launcher: seam.v1.launcher, deployBlock: seam.v1.deployBlock } : undefined
  return v1 ? { launcher: seam.launcher, deployBlock: seam.deployBlock, v1 } : { launcher: seam.launcher, deployBlock: seam.deployBlock }
}

/** The configuration the pages use (read on each call: the seam is set before any page script runs). readTestSeam returns a seam only on a
 * Stage build, so a seam here means the build is Stage. */
export function getCad1Config(): Cad1Config {
  const seam = readTestSeam()
  return resolveCad1Config(seam ? { ...CAD1_PUBLIC_ENV, NEXT_PUBLIC_APP_ENV: 'stage' } : CAD1_PUBLIC_ENV, seam)
}

/** The launcher for new launches on `chainId`: only BNB Smart Chain has one. */
export function cad1LauncherFor(chainId: number | undefined, config: Cad1Config = getCad1Config()): Address | '' {
  return chainId === CAD1_CHAIN_ID ? config.launcher : ''
}

/** Menu entry and home link: the Stage build, or a build with either launcher configured. */
export const isCad1Visible = (env: Record<string, string | undefined> = CAD1_PUBLIC_ENV): boolean =>
  isStage(env) || Boolean(toAddress(env.NEXT_PUBLIC_CAD1_LAUNCHER_ADDRESS)) || Boolean(toAddress(env.NEXT_PUBLIC_CAD1_LAUNCHER_V2_ADDRESS))
export const CAD1_VISIBLE = isCad1Visible()

/**
 * The launcher is live on this build (Bez 2026-10-10, after his live test: "the test is finished"): a v2 launcher (the one open for new
 * launches) is configured, so the menu, the home strip and tile and the /cad1 meta drop "In testing". A build without it (production today,
 * a Stage preview build) keeps the testing labels. The pages' own Preview tag follows the launcher's reads (logic/mode.ts), not this.
 */
export const isCad1Live = (env: Record<string, string | undefined> = CAD1_PUBLIC_ENV): boolean => Boolean(toAddress(env.NEXT_PUBLIC_CAD1_LAUNCHER_V2_ADDRESS))
export const CAD1_LIVE = isCad1Live()

/**
 * The /cad1 routes' data function (review H4): a build that does not show CAD1 (production before Bez opens the soft launch) answers 404
 * from the server instead of serving the pages with index,follow; the Stage build, or one with a launcher, serves them.
 */
export const cad1PageGate = (visible: boolean = CAD1_VISIBLE): { props: Record<string, never> } | { notFound: true } => (visible ? { props: {} } : { notFound: true })

const BSCSCAN = 'https://bscscan.com'

/** Site paths and explorer links. Success links are built from the receipt's Launched event, never from an index. */
export const cad1Paths = {
  home: '/cad1',
  create: '/cad1/create',
  launches: '/cad1/launches',
  tx: (hash: string) => `/cad1/tx/${hash}`,
  token: (token: string) => `/token/${token}`,
  lock: (lockId: bigint | number | string) => `/lock/v3/${lockId}`,
  lockManage: (lockId: bigint | number | string) => `/lock/v3/${lockId}?tab=manage`,
  swap: (token: string) => `/swap?outputCurrency=${token}`,
  competition: (pool: string) => `/trading-competition-v3/create?pool=${pool}`,
  bscTx: (hash: string) => `${BSCSCAN}/tx/${hash}`,
  bscAddress: (address: string) => `${BSCSCAN}/address/${address}`,
} as const
